import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import { KycApiService, SendOtpResponse } from 'src/app/auth/services/kyc-api.service';
import { UploadState } from './components/update-field-file/update-field-file.component';
import {
  DataUpdateRequest,
  DataUpdateRequestItem,
  FieldType,
  getItemId,
  ItemStatus,
  RequestEstado,
} from '../../interfaces/data-update-request.interface';
import { DataUpdateRequestService } from '../../services/data-update-request.service';
import {
  deriveGroupKey,
  FieldGroupKey,
  FILE_FIELDS,
  humanizeFieldKey,
  isDateFieldKey,
  normalizeToDateInput,
  PHONE_FIELD_KEY,
  PHONE_PREFIX_FIELD_KEY,
  sanitizeFieldValue,
  sanitizePhoneNumber,
  sanitizePhonePrefix,
  sortItemsByStatus,
} from './data-update-request.utils';

type ScreenState =
  | 'loading'
  | 'ready'
  | 'submitting'
  | 'awaitingReview'
  | 'completed'
  | 'maxRejects'
  | 'cancelled'
  | 'notFound'
  | 'error';

interface FieldGroup {
  key: FieldGroupKey;
  label: string;
  icon: string;
  items: DataUpdateRequestItem[];
}

const GROUP_META: Record<FieldGroupKey, { label: string; icon: string; order: number }> = {
  personal: { label: 'Datos personales', icon: 'person', order: 1 },
  document: { label: 'Documento de identidad', icon: 'badge', order: 2 },
  selfie: { label: 'Selfie de verificación', icon: 'face', order: 3 },
  company: { label: 'Datos de la empresa', icon: 'business', order: 4 },
  other: { label: 'Otros datos', icon: 'info', order: 99 },
};

/**
 * Pantalla del cliente: "Tienes datos por actualizar".
 *
 * Punto único de entrada cuando `DataUpdateBlockGuard` redirige por
 * `bloqueado === true`. Render dinámico según items que devuelva el
 * backend en `GET /data-update-requests/:id`.
 *
 * Items texto/número/fecha/booleano se envían en batch con
 * `POST /:id/submit`. Items de archivo (`fotoIdfrontal` / `fotoIdPosterior`
 * / `selfie`) usan flujo de 3 pasos (upload-url → PUT S3 → file-confirm)
 * delegado al sub-componente `UpdateFieldFileComponent`.
 *
 * Tras cualquier submit/confirm exitoso, el service refresca el status:
 * si el operador aprobó todo y `bloqueado === false`, el componente
 * detecta el cambio (estado `completed`) y muestra el CTA "Volver al
 * inicio".
 */
@Component({
  selector: 'app-data-update-request',
  templateUrl: './data-update-request.component.html',
  styleUrls: ['./data-update-request.component.sass'],
})
export class DataUpdateRequestComponent implements OnInit, OnChanges, OnDestroy {
  /**
   * Opcional. Cuando el componente se usa embebido (por ej. dentro de
   * `UserHomeComponent`), el padre pasa el `requestId` por @Input. Si no
   * viene, el componente lo lee del route param `:id` (ruta directa
   * `/wallet/client/data-update-request/:id`).
   */
  @Input() requestId: string = '';

  /**
   * `true` cuando el componente está embebido (sin route param). El
   * comportamiento de los botones "Volver al inicio" cambia:
   *  - embebido + estado terminal (completed, etc.) → no se muestra el
   *    botón porque el cliente ya está en el home.
   *  - embebido + `awaitingReview` → SÍ se muestra el botón y emite
   *    `acknowledged` para que el padre lo procese (el home muestra
   *    banner compacto + dashboard normal).
   *  - no embebido → siempre navega al home.
   */
  @Input() embedded: boolean = false;

  /**
   * Emitido cuando el cliente, parado en `awaitingReview`, hace click en
   * "Volver al inicio". El padre (`UserHomeComponent`) reacciona
   * ocultando el embed gigante y mostrando un banner compacto encima del
   * dashboard normal. Si el operador rechaza algún item después, el
   * status vuelve a `ready` y el banner se reemplaza por el form completo.
   */
  @Output() acknowledged = new EventEmitter<void>();

  /**
   * Emitido cada vez que el estado interno cambia (ver `ScreenState`).
   * El padre lo usa para saber cuándo resetear su flag local de
   * "review acknowledged" si vuelven items por completar.
   */
  @Output() stateChange = new EventEmitter<ScreenState>();

  state: ScreenState = 'loading';
  errorMessage: string | null = null;

  request: DataUpdateRequest | null = null;

  /** Grupos ordenados de items, derivados del `request.items`. */
  groups: FieldGroup[] = [];

  /** Sólo items texto/número/date/boolean — los files se manejan aparte. */
  scalarForm: FormGroup = new FormGroup({});

  /** Días restantes para vencer (positivo) o vencida (negativo). */
  daysToDeadline: number = 0;

  /**
   * Estados actuales de cada item de archivo — trackeados vía el
   * `(uploadStateChange)` del sub-componente `<app-update-field-file>`.
   * Se usan en `canSubmit` para bloquear el envío mientras hay uploads
   * en curso o en error.
   */
  private fileUploadStates = new Map<string, UploadState>();

  /**
   * IDs de items de archivo que el cliente ya subió y confirmó con
   * éxito en esta sesión (el `file-confirm` respondió 200). Los tratamos
   * como satisfechos en `canSubmit` aunque el `status` que devuelva el
   * backend en la recarga siga en `PENDING`: el pipeline de OCR /
   * verificación facial es asíncrono y el item puede tardar en pasar a
   * `SUBMITTED`. Sin esto, el cliente ve la foto cargada pero el botón
   * "Enviar para revisión" queda bloqueado diciendo que el documento
   * falta — y nunca llega al paso de OTP.
   */
  private locallyConfirmedFiles = new Set<string>();

  /**
   * Polling automático que dispara `service.refresh()` cada minuto
   * mientras el cliente está esperando o tiene items por completar.
   * Si el operador aprueba mientras la pantalla está abierta, el
   * BehaviorSubject del service cambia y el padre (UserHomeComponent)
   * libera la vista — sin que el cliente tenga que tocar nada.
   */
  private readonly STATUS_POLL_MS = 60_000;
  private statusPollHandle: any = null;

  // ── OTP WhatsApp para verificar cambio de teléfono ─────────────────

  /**
   * Cuando la solicitud incluye `telefono`/`prefijoTelefono`, el
   * cliente debe verificar el número nuevo con un OTP por WhatsApp.
   * El front solo PIDE el código (send/resend) y lo manda en el body
   * del submit (`phoneOtpCode`); quien lo VERIFICA es wallet-api, con
   * el mismo número que va a guardar. Si lo verificara el browser,
   * bastaría con saltearse el front para registrar un número sin
   * verificar (y además el KYC consume el código: verificarlo acá
   * haría que el servidor recibiera NO_PENDING).
   */
  showOtpModal: boolean = false;
  otpCode: string = '';
  otpTargetPhone: string = '';
  otpLoading: boolean = false;
  otpError: string | null = null;
  otpResendCooldown: number = 0;
  private otpResendTimer: any = null;
  /**
   * Payload del submit pendiente hasta que se verifique el OTP. Cuando
   * el cliente confirma el código, disparamos el submit real con este
   * payload. Sin esto tendríamos que re-armar el payload post-OTP y
   * podría no coincidir.
   */
  private pendingSubmitItems: { itemId: string; value: any }[] = [];

  /**
   * Teléfono actual del usuario logueado (store `auth`). Se usa para
   * completar el número a verificar cuando la solicitud pide solo una
   * parte del par (ej. el número sin el prefijo): el backend completa
   * igual con el valor del usuario, y si acá no lo hiciéramos no habría
   * a qué número mandar el OTP.
   */
  private authUserPhone: { prefijoTelefono?: string | null; telefono?: string | null } | null = null;
  private authSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: DataUpdateRequestService,
    private kycApiService: KycApiService,
    private store: Store<AppState>,
  ) {
    this.authSub = this.store.select('auth').subscribe(({ user }: any) => {
      this.authUserPhone = user
        ? { prefijoTelefono: user.prefijoTelefono, telefono: user.telefono }
        : null;
    });
  }

  // ────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ────────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    if (!this.requestId) {
      this.requestId = this.route.snapshot.paramMap.get('id') || '';
    }
    if (!this.requestId) {
      this.state = 'notFound';
      return;
    }
    this.loadDetail();
    this.startStatusPolling();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Cuando el padre cambia el `requestId` por @Input (cliente terminó
    // una solicitud y el operador abrió otra), recargamos el detalle.
    if (
      changes['requestId'] &&
      !changes['requestId'].firstChange &&
      this.requestId
    ) {
      // Otra solicitud: las confirmaciones locales de la anterior ya no
      // aplican.
      this.locallyConfirmedFiles.clear();
      this.loadDetail();
    }
  }

  ngOnDestroy(): void {
    this.stopStatusPolling();
    this.stopOtpResendCooldown();
    this.authSub?.unsubscribe();
  }

  /**
   * Arranca un setInterval que pinga `/me` cada minuto. Sólo corre
   * mientras estamos en estados "abiertos" — si la solicitud se cierra
   * (completed/cancelled/maxRejects) cortamos el polling para no pegar
   * inútilmente.
   */
  private startStatusPolling(): void {
    if (this.statusPollHandle) return;
    this.statusPollHandle = setInterval(() => {
      const isOpen =
        this.state === 'ready' ||
        this.state === 'awaitingReview' ||
        this.state === 'submitting';
      if (!isOpen) {
        this.stopStatusPolling();
        return;
      }
      // refresh() actualiza el BehaviorSubject; si el operador completó
      // todo, el subject pasará a `bloqueado=false` y el padre va a
      // desmontarnos. Si todavía hay solicitud, además recargamos el
      // detalle para reflejar items aprobados/rechazados.
      this.service.refresh().then(() => {
        // NO recargamos el detalle si el cliente está editando el form
        // (dirty): `loadDetail` reconstruye el FormGroup y le borraría lo
        // que escribió y todavía no envió. El refresh() de arriba ya cubre
        // el caso principal (operador completó todo → el padre nos
        // desmonta). Cuando no hay edición en curso, recargamos en
        // silencio (sin el flicker de la pantalla de "loading") para
        // reflejar items aprobados/rechazados.
        if (this.scalarForm.dirty) return;
        if (this.state === 'awaitingReview' || this.state === 'ready') {
          this.loadDetail({ silent: true });
        }
      });
    }, this.STATUS_POLL_MS);
  }

  private stopStatusPolling(): void {
    if (this.statusPollHandle) {
      clearInterval(this.statusPollHandle);
      this.statusPollHandle = null;
    }
  }

  /**
   * @param options.silent cuando es `true` no se cambia el estado a
   *   `loading` (evita el parpadeo a spinner). Lo usa el polling de
   *   status para refrescar en segundo plano sin interrumpir al cliente.
   */
  async loadDetail(options: { silent?: boolean } = {}): Promise<void> {
    const silent = options.silent === true;
    if (!silent) {
      this.state = 'loading';
      this.stateChange.emit(this.state);
    }
    this.errorMessage = null;
    try {
      // Este componente es del cliente. Usamos `getMyDetail` que apunta
      // a `GET /data-update-requests/me/:id` — el backend valida que la
      // solicitud sea del usuario del token. El endpoint sin `/me/` es
      // sólo de operador y devuelve 401 para clientes.
      const detail = await this.service.getMyDetail(this.requestId);
      this.request = detail;
      // Si el operador rechazó un archivo que habíamos marcado como
      // confirmado localmente, el rechazo manda: lo sacamos del set para
      // volver a exigir que el cliente suba uno nuevo.
      for (const it of detail.items) {
        if (it.status === 'REJECTED') {
          this.locallyConfirmedFiles.delete(getItemId(it));
        }
      }
      this.daysToDeadline = this.computeDaysToDeadline(detail.deadlineAt);
      this.groups = this.buildGroups(detail.items);
      // Pasamos el form actual para preservar lo que el cliente esté
      // escribiendo (controles dirty) frente a recargas — el backend no
      // conoce todavía esos valores sin enviar.
      this.scalarForm = this.buildScalarForm(detail.items, this.scalarForm);
      this.state = this.deriveStateFromRequest(detail);
      this.stateChange.emit(this.state);
    } catch (err: any) {
      const status = err?.status;
      // 401 lo agarra el ErrorInterceptor global (sesión expirada,
      // redirige a login). Para el resto distinguimos según el contrato
      // del endpoint `/me/:id`:
      //   - 403 → la solicitud existe pero no es del cliente.
      //   - 404 → el id no existe (ya cancelada o nunca existió).
      //   - resto → mensaje genérico amigable.
      if (status === 404) {
        this.state = 'notFound';
      } else if (status === 403) {
        this.state = 'error';
        this.errorMessage =
          'Esta solicitud no está disponible para tu cuenta. Si creés que es un error, contactá a soporte.';
      } else {
        this.state = 'error';
        this.errorMessage =
          err?.error?.message ||
          err?.message ||
          'No pudimos cargar tu solicitud. Reintentá en unos segundos.';
      }
    }
  }

  // ────────────────────────────────────────────────────────────────────
  // Construcción del form dinámico
  // ────────────────────────────────────────────────────────────────────

  private buildGroups(items: DataUpdateRequestItem[]): FieldGroup[] {
    const map = new Map<FieldGroupKey, DataUpdateRequestItem[]>();
    for (const item of items) {
      const key = deriveGroupKey(item.fieldKey);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.entries())
      .map(([key, groupItems]) => ({
        key,
        label: GROUP_META[key].label,
        icon: GROUP_META[key].icon,
        // Dentro de cada sección, ordenamos por ciclo de vida:
        // SUBMITTED → PENDING → REJECTED → APPROVED. Le da al cliente
        // arriba los items donde ya interactuó y abajo los cerrados.
        items: sortItemsByStatus(groupItems),
      }))
      .sort((a, b) => GROUP_META[a.key].order - GROUP_META[b.key].order);
  }

  /**
   * Build FormGroup sólo para items no-archivo en estado editable. Los
   * archivos se manejan fuera del FormGroup (flujo upload propio del
   * sub-componente). Los items en `APPROVED` o `SUBMITTED` quedan
   * read-only en la UI y NO entran al form — no queremos que el cliente
   * pise un valor que ya está en revisión por el operador.
   */
  private buildScalarForm(
    items: DataUpdateRequestItem[],
    previous?: FormGroup,
  ): FormGroup {
    const controls: Record<string, FormControl> = {};
    for (const item of items) {
      if (this.isFileItem(item)) continue;
      if (item.status === 'APPROVED' || item.status === 'SUBMITTED') continue;

      const validators: ValidatorFn[] = [];
      if (item.required !== false) validators.push(Validators.required);
      if (item.fieldType === 'email') validators.push(Validators.email);

      let initial: string | number | boolean =
        item.submittedValue !== undefined && item.submittedValue !== null
          ? item.submittedValue
          : '';
      // Normalización de fechas: el backend puede devolver el
      // `submittedValue` como ISO completo ("1984-03-09T00:00:00.000Z");
      // el `<input type="date">` sólo acepta "YYYY-MM-DD". Convertimos
      // sin shift de zona horaria para preservar el día real.
      const isDate =
        item.fieldType === 'date' || isDateFieldKey(item.fieldKey);
      if (isDate && initial !== '') {
        initial = normalizeToDateInput(initial);
      }
      // El backend devuelve el identificador como `id`; algunos endpoints
      // legacy podrían usar `itemId`. Usamos el helper para cubrir ambos.
      // Sin este mapeo, el key del FormControl quedaba `undefined` y el
      // input renderizaba sin control asociado → input bloqueado.
      const key = getItemId(item);
      if (!key) continue;

      // Si ya existía un control para este item y el cliente lo modificó
      // (dirty) sin enviar todavía, conservamos su valor en lugar de
      // pisarlo con el `submittedValue` del backend. Sin esto, cualquier
      // recarga (polling de status, confirmación de archivo, etc.) borra
      // lo que el usuario estaba escribiendo.
      const prevCtrl = previous?.get(key);
      const control = new FormControl(initial, validators);
      if (prevCtrl instanceof FormControl && prevCtrl.dirty) {
        control.setValue(prevCtrl.value, { emitEvent: false });
        control.markAsDirty();
        if (prevCtrl.touched) control.markAsTouched();
      }
      controls[key] = control;
    }
    return new FormGroup(controls);
  }

  // ────────────────────────────────────────────────────────────────────
  // Derivación de estado
  // ────────────────────────────────────────────────────────────────────

  private deriveStateFromRequest(req: DataUpdateRequest): ScreenState {
    if (req.estado === 'COMPLETED') return 'completed';
    if (req.estado === 'CANCELLED') return 'cancelled';
    if (req.estado === 'CLOSED_MAX_REJECTS') return 'maxRejects';

    // Estado "awaitingReview": todos los items terminados están en
    // SUBMITTED (esperando aprobación del operador) y NO hay nada
    // pendiente ni rechazado que el cliente tenga que completar.
    // En este caso mostramos un cartel "tu información está en revisión"
    // en lugar del form completo — el cliente ya no tiene nada que hacer.
    const hasActionable = req.items.some(
      (i) => i.status === 'PENDING' || i.status === 'REJECTED',
    );
    const hasSubmitted = req.items.some((i) => i.status === 'SUBMITTED');
    if (!hasActionable && hasSubmitted) return 'awaitingReview';

    return 'ready';
  }

  // ────────────────────────────────────────────────────────────────────
  // Submit batch de items escalares
  // ────────────────────────────────────────────────────────────────────

  async submit(): Promise<void> {
    if (!this.request) return;
    if (this.scalarForm.invalid) {
      this.scalarForm.markAllAsTouched();
      Swal.fire({
        icon: 'warning',
        title: 'Faltan datos',
        text: 'Completá los campos requeridos antes de enviar.',
        confirmButtonColor: '#1e5a3f',
      });
      return;
    }

    const itemIds = Object.keys(this.scalarForm.controls);
    if (itemIds.length === 0) {
      // Sólo había items de archivo (que ya se confirmaron por su cuenta) —
      // no hay nada que mandar. Refrescamos para detectar transición.
      await this.service.refresh();
      await this.loadDetail();
      return;
    }

    // Sanitizamos cada valor según el `fieldKey` del item ANTES de
    // enviar. Casos especiales:
    //   - `telefono` → sólo dígitos (sin espacios, guiones ni signos).
    //   - `prefijoTelefono` → `+` + dígitos.
    //   - Otros strings → trim para eliminar padding accidental.
    // El resultado se refleja también en el FormControl para que el
    // input quede alineado con lo que efectivamente se envía.
    const items = itemIds.map((itemId) => {
      const rawValue = this.scalarForm.value[itemId];
      const item = this.request?.items.find((it) => getItemId(it) === itemId);
      const fieldKey = item?.fieldKey ?? '';
      const sanitized = sanitizeFieldValue(fieldKey, rawValue);
      if (sanitized !== rawValue) {
        this.scalarForm.get(itemId)?.setValue(sanitized, { emitEvent: false });
      }
      return { itemId, value: sanitized };
    });

    // Si la solicitud incluye un cambio de teléfono, disparamos el
    // flujo OTP antes de enviar al backend. Sólo se avanza al submit
    // real cuando el cliente verifica el código correctamente.
    const phone = this.extractPhoneFromItems(items);
    if (phone) {
      this.pendingSubmitItems = items;
      await this.startPhoneOtp(phone);
      return;
    }

    await this.sendSubmit(items);
  }

  /**
   * Ejecución final del submit al backend. Extraída de `submit()` para
   * poder invocarla desde el flujo OTP con el código ingresado.
   *
   * `phoneOtpCode` viaja en el body: lo verifica wallet-api. Cuando viene
   * (flujo OTP), los errores se muestran DENTRO del modal (`otpError`) en
   * vez de un Swal, para que el cliente corrija el código sin volver a
   * empezar. Devuelve `true` solo si el submit se aceptó.
   */
  private async sendSubmit(
    items: { itemId: string; value: any }[],
    opts: { phoneOtpCode?: string; isRetry?: boolean } = {},
  ): Promise<boolean> {
    if (!this.request) return false;
    const { phoneOtpCode, isRetry = false } = opts;
    this.state = 'submitting';
    try {
      await this.service.submit(this.request.id, {
        items,
        ...(phoneOtpCode ? { phoneOtpCode } : {}),
      });
      // NO mostramos Swal de éxito acá: la transición visual al estado
      // `awaitingReview` (con la ilustración pulsante y el mensaje "Tu
      // información está en revisión") es suficiente feedback.
      await this.loadDetail();
      return true;
    } catch (err: any) {
      // Ids obsoletos (solicitud re-emitida): remapeamos por fieldKey con
      // ids frescos y reintentamos UNA vez. Misma regla que los archivos.
      // El código OTP se reenvía tal cual: el backend valida los items
      // ANTES de verificarlo, así que este rechazo no lo consumió.
      if (this.service.isItemNoVigente(err) && !isRetry) {
        const remapped = await this.remapItemsFresh(items);
        if (remapped) {
          return this.sendSubmit(remapped, { phoneOtpCode, isRetry: true });
        }
        // El fieldKey ya no está entre los items → la solicitud cambió.
        // El modal OTP (si estaba abierto) ya no aplica: se cierra.
        if (phoneOtpCode) this.closeOtpModal();
        this.state = 'ready';
        await this.loadDetail();
        Swal.fire({
          icon: 'warning',
          title: 'La solicitud cambió',
          text: 'Los datos solicitados se actualizaron. Revisá los campos vigentes y volvé a enviar.',
          confirmButtonColor: '#1e5a3f',
        });
        return false;
      }
      this.state = 'ready';
      const message = submitErrorMessage(err);
      if (phoneOtpCode) {
        // Código inválido/vencido, KYC caído, etc.: el modal queda abierto
        // con el motivo que devolvió el backend.
        this.otpError = message;
        return false;
      }
      Swal.fire({
        icon: 'error',
        title: 'No pudimos enviar',
        text: message,
        confirmButtonColor: '#1e5a3f',
      });
      return false;
    }
  }

  /**
   * Remapea un payload de submit a los `itemId` FRESCOS resolviendo por
   * `fieldKey` contra la solicitud vigente (`GET /me`). Devuelve `null` si
   * no hay solicitud o algún `fieldKey` ya no está entre los items (la
   * solicitud cambió y ese dato ya no se pide).
   */
  private async remapItemsFresh(
    items: { itemId: string; value: any }[],
  ): Promise<{ itemId: string; value: any }[] | null> {
    const fresh = await this.service.getMyCurrent();
    if (!fresh) return null;
    const out: { itemId: string; value: any }[] = [];
    for (const it of items) {
      // fieldKey del item viejo (según el request cargado en ese momento).
      const oldItem = this.request?.items.find(
        (x) => getItemId(x) === it.itemId,
      );
      const fieldKey = oldItem?.fieldKey;
      if (!fieldKey) return null;
      const freshItem = fresh.items.find((x) => x.fieldKey === fieldKey);
      if (!freshItem) return null;
      const newId = getItemId(freshItem);
      if (!newId) return null;
      out.push({ itemId: newId, value: it.value });
    }
    return out;
  }

  // ────────────────────────────────────────────────────────────────────
  // OTP WhatsApp — verificación de teléfono
  // ────────────────────────────────────────────────────────────────────

  /**
   * Extrae el número completo (prefijo + teléfono) del payload si la
   * solicitud incluye el par teléfono/prefijo. Si sólo se envía uno,
   * completa con el valor actual del item pareja para no perder
   * información. Devuelve `null` si no hay cambio de teléfono.
   */
  private extractPhoneFromItems(
    items: { itemId: string; value: any }[],
  ): string | null {
    if (!this.request) return null;

    const findItemByFieldKey = (key: string): DataUpdateRequestItem | null =>
      this.request!.items.find((it) => it.fieldKey === key) ?? null;

    const phoneItem = findItemByFieldKey(PHONE_FIELD_KEY);
    const prefixItem = findItemByFieldKey(PHONE_PREFIX_FIELD_KEY);

    const submittedPhone = phoneItem
      ? items.find((i) => i.itemId === getItemId(phoneItem))?.value
      : null;
    const submittedPrefix = prefixItem
      ? items.find((i) => i.itemId === getItemId(prefixItem))?.value
      : null;

    // Sin item de teléfono ni prefijo → no hay verificación necesaria.
    if (submittedPhone == null && submittedPrefix == null) return null;

    // Si sólo viaja una parte del par, se completa con la otra con la
    // MISMA regla que usa wallet-api para armar el número que verifica
    // (si difieren, el backend verificaría otro número y rechazaría):
    //   1. el valor ya enviado y en revisión/aprobado del item pareja,
    //   2. si no, el valor actual del usuario (store `auth`),
    //   3. y como último recurso el `currentValue` del item.
    // Sanitizamos cada parte con los helpers dedicados para que la
    // concatenación garantice el formato `+56948608491` sin espacios,
    // guiones ni caracteres exóticos. El backend compara por dígitos.
    const pairValue = (
      item: DataUpdateRequestItem | null,
      userValue: string | null | undefined,
    ): unknown => {
      if (
        item &&
        (item.status === 'SUBMITTED' || item.status === 'APPROVED') &&
        item.submittedValue != null
      ) {
        return item.submittedValue;
      }
      return userValue ?? item?.currentValue;
    };
    const phone = sanitizePhoneNumber(
      submittedPhone ?? pairValue(phoneItem, this.authUserPhone?.telefono),
    );
    const prefix = sanitizePhonePrefix(
      submittedPrefix ??
        pairValue(prefixItem, this.authUserPhone?.prefijoTelefono),
    );
    if (!phone || !prefix) return null;
    return `${prefix}${phone}`;
  }

  /** Abre el modal OTP y dispara el envío del código por WhatsApp. */
  private async startPhoneOtp(phone: string): Promise<void> {
    this.otpTargetPhone = phone;
    this.otpCode = '';
    this.otpError = null;
    this.otpLoading = true;
    this.showOtpModal = true;
    try {
      // `sendOtp` NO lanza: los rechazos vuelven como `success:false`.
      this.applyOtpSendResult(
        await this.kycApiService.sendOtp(phone),
        'No pudimos enviar el código por WhatsApp. Revisá que el número sea correcto.',
      );
    } finally {
      this.otpLoading = false;
    }
  }

  /**
   * Manda el código al backend junto con el submit. NO llama a
   * `kycApiService.verifyOtp`: el KYC consume el código al verificarlo,
   * y wallet-api (que es quien decide) recibiría NO_PENDING.
   */
  async verifyPhoneOtp(): Promise<void> {
    const code = (this.otpCode || '').trim();
    if (!/^\d{6}$/.test(code)) {
      this.otpError = 'El código tiene 6 dígitos.';
      return;
    }
    this.otpLoading = true;
    this.otpError = null;
    try {
      // Snapshot de los items: `closeOtpModal()` limpia
      // `pendingSubmitItems` como parte del reset general. Ahora el modal
      // se cierra recién tras el éxito, pero si se cerrara mientras el
      // submit viaja, sin este snapshot se enviaría `[]` y el backend
      // respondería "items must contain at least 1 elements".
      const itemsToSubmit = [...this.pendingSubmitItems];
      const ok = await this.sendSubmit(itemsToSubmit, { phoneOtpCode: code });
      // Solo se cierra si el backend aceptó; si no, `sendSubmit` dejó el
      // motivo en `otpError` y el modal sigue abierto para reintentar.
      if (ok && this.showOtpModal) this.closeOtpModal();
    } finally {
      this.otpLoading = false;
    }
  }

  async resendPhoneOtp(): Promise<void> {
    if (this.otpResendCooldown > 0 || this.otpLoading) return;
    this.otpLoading = true;
    this.otpError = null;
    try {
      this.applyOtpSendResult(
        await this.kycApiService.resendOtp(this.otpTargetPhone),
        'No pudimos reenviar el código. Reintentá en unos segundos.',
      );
    } finally {
      this.otpLoading = false;
    }
  }

  /**
   * Refleja el resultado de send/resend en el modal. Si falló se muestra
   * el motivo del KYC y el cooldown arranca solo si el backend informa
   * cuánto esperar (`resendInSeconds`, ej. COOLDOWN/RATE_LIMITED): un
   * cooldown fijo tras un fallo (ej. WhatsApp caído) bloqueaba el
   * reintento sin motivo. Si salió, se usa el cooldown del backend.
   */
  private applyOtpSendResult(
    response: SendOtpResponse,
    fallbackError: string,
  ): void {
    if (response?.success) {
      this.startOtpResendCooldown(response.resendInSeconds ?? 60);
      return;
    }
    // Con WhatsApp caído el cliente no puede verificar el celular por su
    // cuenta: se lo deriva al operador (mismo texto que en "Mi perfil").
    this.otpError =
      response?.code === 'UNAVAILABLE'
        ? 'Para actualizar tu celular, comunícate con el operador para activar el OTP.'
        : response?.message || fallbackError;
    if (response?.resendInSeconds && response.resendInSeconds > 0) {
      this.startOtpResendCooldown(response.resendInSeconds);
    }
  }

  closeOtpModal(): void {
    this.showOtpModal = false;
    this.otpCode = '';
    this.otpError = null;
    this.otpLoading = false;
    this.otpTargetPhone = '';
    this.stopOtpResendCooldown();
    // Si el cliente cierra el modal sin verificar, descartamos el
    // payload pendiente. Al re-clickear "Enviar para revisión" se
    // arma de nuevo.
    this.pendingSubmitItems = [];
  }

  private startOtpResendCooldown(seconds: number): void {
    this.stopOtpResendCooldown();
    this.otpResendCooldown = Math.max(1, Math.ceil(seconds));
    this.otpResendTimer = setInterval(() => {
      this.otpResendCooldown--;
      if (this.otpResendCooldown <= 0) {
        this.stopOtpResendCooldown();
      }
    }, 1000);
  }

  private stopOtpResendCooldown(): void {
    if (this.otpResendTimer) {
      clearInterval(this.otpResendTimer);
      this.otpResendTimer = null;
    }
    this.otpResendCooldown = 0;
  }

  // ────────────────────────────────────────────────────────────────────
  // Callbacks de sub-componentes
  // ────────────────────────────────────────────────────────────────────

  async onFileConfirmed(itemId?: string): Promise<void> {
    // El sub-componente de archivo ya hizo el confirm (200). Marcamos el
    // item como satisfecho localmente para que `canSubmit` no lo cuente
    // como faltante aunque el backend todavía lo devuelva en PENDING
    // (OCR/verificación asíncrona). Luego recargamos el detalle para que
    // el chip de status se actualice cuando el backend termine.
    if (itemId) this.locallyConfirmedFiles.add(itemId);
    await this.loadDetail({ silent: true });
  }

  /**
   * Handler del `(uploadStateChange)` del sub-componente de archivos.
   * Trackeamos el estado por `itemId` para que `canSubmit` pueda
   * bloquear el envío cuando hay uploads en curso o con error.
   */
  onFileUploadStateChange(itemId: string, state: UploadState): void {
    if (!itemId) return;
    this.fileUploadStates.set(itemId, state);
  }

  /**
   * `true` sólo cuando la solicitud está lista para enviarse:
   *   1. El componente está en estado `ready` (no submitting/loading).
   *   2. El form escalar es válido (todos los campos de texto/fecha/
   *      número/prefijo/boolean requeridos completos y bien).
   *   3. Ningún archivo está en upload/confirming/error localmente.
   *   4. Todos los items de archivo requeridos están fuera de
   *      `PENDING`/`REJECTED` (o sea, ya se subieron y confirmaron).
   *
   * El botón "Enviar para revisión" se habilita únicamente si esto es
   * `true`. Antes bastaba con `!scalarForm.invalid` pero eso ignoraba
   * los archivos — el cliente podía intentar enviar sin subirlos.
   */
  get canSubmit(): boolean {
    if (this.state !== 'ready') return false;
    if (!this.request) return false;

    // Escalares: form completo y sin errores de validators.
    if (this.scalarForm.invalid) return false;

    // Archivos locales: ningún upload/confirm en curso ni con error.
    for (const st of this.fileUploadStates.values()) {
      if (st !== 'idle') return false;
    }

    // Archivos requeridos: cada file item obligatorio debe estar en
    // SUBMITTED o APPROVED. Si sigue en PENDING o REJECTED significa
    // que el cliente NO lo subió (o el operador lo rechazó y el
    // cliente todavía no volvió a mandar).
    const filesPendingRequired = this.request.items.filter(
      (it) =>
        this.isFileItem(it) &&
        it.required !== false &&
        (it.status === 'PENDING' || it.status === 'REJECTED') &&
        // Ya subido y confirmado en esta sesión → no lo bloqueamos aunque
        // el backend siga devolviendo el status desactualizado.
        !this.locallyConfirmedFiles.has(getItemId(it)),
    );
    if (filesPendingRequired.length > 0) return false;

    return true;
  }

  /**
   * Mensaje explicativo del porqué el submit está deshabilitado.
   * Se muestra debajo del botón cuando `canSubmit === false` para
   * guiar al cliente sin que tenga que adivinar qué le falta.
   */
  get submitBlockedReason(): string | null {
    if (this.canSubmit) return null;
    if (this.state === 'submitting') return null;
    if (!this.request) return null;

    if (this.scalarForm.invalid) {
      return 'Completá todos los campos requeridos antes de enviar.';
    }
    for (const st of this.fileUploadStates.values()) {
      if (st === 'uploading' || st === 'confirming') {
        return 'Esperá a que termine la subida del archivo.';
      }
      if (st === 'error') {
        return 'Hay un archivo con error de subida. Reintentá antes de enviar.';
      }
    }
    const missingFiles = this.request.items.filter(
      (it) =>
        this.isFileItem(it) &&
        it.required !== false &&
        (it.status === 'PENDING' || it.status === 'REJECTED') &&
        !this.locallyConfirmedFiles.has(getItemId(it)),
    );
    if (missingFiles.length === 1) {
      return `Falta subir: ${this.labelOf(missingFiles[0])}.`;
    }
    if (missingFiles.length > 1) {
      return `Faltan subir ${missingFiles.length} documentos.`;
    }
    return null;
  }

  // ────────────────────────────────────────────────────────────────────
  // Helpers UI
  // ────────────────────────────────────────────────────────────────────

  goHome(): void {
    this.router.navigate(['/wallet/home']);
  }

  /**
   * Abre WhatsApp con un mensaje pre-cargado dirigido al soporte de
   * Aguacate. Se usa desde el estado `maxRejects` para que el cliente
   * pueda contactar al equipo sin tener que copiar/pegar nada.
   *
   * Número alineado con el resto del proyecto (navbar/zelle-home).
   */
  openWhatsAppSupport(): void {
    const phone = '56965474761';
    const message = 'Necesito ayuda con mi solicitud de actualizacion de datos';
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * Llamado desde el botón "Volver al inicio" en el estado
   * `awaitingReview`. Si estamos embebidos, emitimos el evento para que
   * el padre maneje la transición (banner compacto). Si estamos en la
   * ruta directa, navegamos al home.
   */
  acknowledgeReview(): void {
    if (this.embedded) {
      this.acknowledged.emit();
    } else {
      this.goHome();
    }
  }

  retryLoad(): void {
    this.loadDetail();
  }

  isFileItem(item: DataUpdateRequestItem): boolean {
    return item.fieldType === 'file' || FILE_FIELDS.has(item.fieldKey);
  }

  labelOf(item: DataUpdateRequestItem): string {
    // Override específico del formulario cliente: cuando se pide el
    // teléfono, dejamos claro que es el número de WhatsApp — el mismo
    // por el que va a recibir el código de verificación. En el catálogo
    // del operador sigue apareciendo como "Teléfono" a secas.
    if (item.fieldKey === PHONE_FIELD_KEY) return 'Teléfono WhatsApp';
    // `humanizeFieldKey` consulta primero el diccionario manual y cae
    // al algoritmo camelCase → "Camel case" si no encuentra el field.
    return item.label || humanizeFieldKey(item.fieldKey);
  }

  /**
   * Cuenta de items PENDING (incluye REJECTED que vuelven a pendiente).
   */
  get itemsPending(): number {
    if (!this.request) return 0;
    return this.request.items.filter(
      (i) => i.status === 'PENDING' || i.status === 'REJECTED',
    ).length;
  }

  get itemsTotal(): number {
    return this.request?.items.length ?? 0;
  }

  get itemsCompleted(): number {
    if (!this.request) return 0;
    return this.request.items.filter(
      (i) => i.status === 'APPROVED' || i.status === 'SUBMITTED',
    ).length;
  }

  get progressPercent(): number {
    if (this.itemsTotal === 0) return 0;
    return Math.round((this.itemsCompleted / this.itemsTotal) * 100);
  }

  get deadlineLabel(): string {
    if (!this.request) return '';
    const days = this.daysToDeadline;
    if (days < 0) return `Vencida hace ${Math.abs(days)} día(s)`;
    if (days === 0) return 'Vence hoy';
    if (days === 1) return 'Vence mañana';
    return `${days} días restantes`;
  }

  get deadlineSeverity(): 'expired' | 'urgent' | 'soon' | 'ok' {
    if (this.daysToDeadline < 0) return 'expired';
    if (this.daysToDeadline <= 1) return 'urgent';
    if (this.daysToDeadline <= 3) return 'soon';
    return 'ok';
  }

  /** Chip de estado de un item — color por estado. */
  statusColor(status: ItemStatus): string {
    switch (status) {
      case 'PENDING':
        return '#f59e0b';
      case 'SUBMITTED':
        return '#3b82f6';
      case 'APPROVED':
        return '#16a34a';
      case 'REJECTED':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  }

  statusLabel(status: ItemStatus): string {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'SUBMITTED':
        return 'En revisión';
      case 'APPROVED':
        return 'Aprobado';
      case 'REJECTED':
        return 'Rechazado';
      default:
        return status;
    }
  }

  estadoLabel(estado: RequestEstado): string {
    switch (estado) {
      case 'PENDING':
        return 'Pendiente';
      case 'COMPLETED':
        return 'Completada';
      case 'EXPIRED':
        return 'Vencida';
      case 'CANCELLED':
        return 'Cancelada';
      case 'CLOSED_MAX_REJECTS':
        return 'Cerrada (máx. reintentos)';
      default:
        return estado;
    }
  }

  controlForItem(item: DataUpdateRequestItem): FormControl | null {
    const key = getItemId(item);
    if (!key) return null;
    const ctrl = this.scalarForm.get(key);
    return ctrl instanceof FormControl ? ctrl : null;
  }

  // ────────────────────────────────────────────────────────────────────
  // Helpers internos
  // ────────────────────────────────────────────────────────────────────

  private computeDaysToDeadline(deadlineAt: string): number {
    if (!deadlineAt) return 0;
    const target = new Date(deadlineAt).getTime();
    const now = Date.now();
    return Math.floor((target - now) / (1000 * 60 * 60 * 24));
  }

  // Used by template
  readonly FieldType = {} as Record<FieldType, FieldType>;
}

/**
 * Mensaje legible de un error del submit. El `ErrorInterceptor` deja
 * pasar el `HttpErrorResponse` completo en 400/422/429, pero para el
 * resto (ej. 503 si el KYC no responde) relanza solo el texto: por eso
 * se contempla también el string. `message` puede venir como array
 * (errores de class-validator).
 */
function submitErrorMessage(err: any): string {
  if (typeof err === 'string' && err.trim()) return err;
  const msg = err?.error?.message ?? err?.message;
  if (Array.isArray(msg) && msg.length) return String(msg[0]);
  if (typeof msg === 'string' && msg.trim()) return msg;
  return 'Reintentá en unos segundos.';
}
